import { redirect } from 'next/navigation';
import qs from 'query-string';

import { CrumbItem } from '../components/features/account/common/bread-crumb';
import { NEW_VERSION_TO_OLD_VERSION_PATHS } from '../constants';

import { isServerSide } from './env';

interface QueryParams {
  [key: string]: string | string[] | undefined;
}

type ParsedUrl = {
  domain: string;
  path: string;
  query: Record<string, string>;
  originQuery: URLSearchParams;
  originPath: string;
};

/**
 * 如果是 edu.net 的域名，返回 edu.net 的 API地址
 * @returns
 */
export const getAPIBaseUrl = (api: string | undefined) => {
  if (!api) {
    return '';
  }
  const isEduUrl = globalThis?.location?.href?.includes?.('.wukongedu.net');
  return isEduUrl ? api.replace('.wukongacademy.com', '.wukongedu.net') : api;
};

/**
 * 去除url上的某个查询参数
 * @param url
 * @param param
 * @returns
 */
export const removeQueryParam = (url: string, param: string) => {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.delete(param);
  return parsedUrl.toString();
};

/**
 * 解析 URL 查询参数
 * @param url - 可选的 URL 字符串，如果不提供则使用当前页面 URL
 * @returns 解析后的查询参数对象
 */
export const getQueryParams = (url?: string): QueryParams => {
  try {
    // 如果提供了 URL，使用 URL 对象解析
    if (url) {
      const urlObj = new URL(url);
      return qs.parse(urlObj.search.slice(1)) as QueryParams;
    }

    // 在浏览器环境下使用 window.location
    if (typeof window !== 'undefined') {
      return qs.parse(window.location.search.slice(1)) as QueryParams;
    }

    return {};
  } catch (error) {
    console.error('解析 URL 参数失败:', error);
    return {};
  }
};

/**
 * 解码 base64 字符串
 * @param str - 需要解码的 base64 字符串
 * @returns 解码后的字符串
 */
export const decodeBase64 = (str: string) => {
  if (!str) {
    return '';
  }
  try {
    // const bytes = Uint8Array.from(window.atob(str), (c) => c.charCodeAt(0));
    // return new TextDecoder('utf-8').decode(bytes);
    return window.atob(str);
  } catch (error) {
    console.error('Base64 解码失败:', error);
    return str;
  }
};

/**
 * 根据url获取当前域名
 * @returns
 */
export const getCurrentDomain = (domain: string | undefined) => {
  if (!domain) {
    return '';
  }

  // 只有线上环境才进行域名替换逻辑
  if (process.env.NEXT_PUBLIC_ENV === 'prod') {
    // 客户端检查，避免 SSR 错误
    if (typeof window === 'undefined') {
      return domain;
    }

    let result = window.location.href.includes('edu.net')
      ? domain.replace('sch.com', 'edu.net')
      : domain.replace('edu.net', 'sch.com');

    if (result.includes('ofw.wukongsch.com')) {
      result = result.replace('ofw.wukongsch.com', 'www.wukongsch.com');
    }

    return result;
  }

  // 非线上环境直接返回传入的域名
  return domain;
};

/**
 * 根据url获取当前域名
 * @returns
 */
export const getCurrentDomainInServer = async (domain: string | undefined) => {
  if (!domain) {
    return '';
  }

  // 只有线上环境才进行域名替换逻辑
  if (process.env.NEXT_PUBLIC_ENV === 'prod') {
    const url = await getBrowserUrl();
    let result = url.includes('edu.net')
      ? domain.replace('sch.com', 'edu.net')
      : domain.replace('edu.net', 'sch.com');

    if (result.includes('ofw.wukongsch.com')) {
      result = result.replace('ofw.wukongsch.com', 'www.wukongsch.com');
    }

    return result;
  }

  // 非线上环境直接返回传入的域名
  return domain;
};

/**
 * 工具：根据路径生成基础面包屑数组（/a/b-c -> [Home, a, b c]）
 * - 可传入 labelMap 覆盖名称。
 */
export function pathToBreadcrumbs(
  pathname: string,
  opts?: {
    baseHref?: string;
    homeLabel?: React.ReactNode;
    labelMap?: Record<string, React.ReactNode>;
  },
): { items: CrumbItem[]; combineKey: string } {
  const base = opts?.baseHref ?? '';
  if (pathname.startsWith('/zh')) {
    pathname = pathname.replace('/zh', '');
  }
  const parts = pathname
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const items: CrumbItem[] = [];

  items.push({
    label: opts?.homeLabel ?? 'Home',
    href: base + '/',
  });

  let acc = '';
  let combineKey = '';
  for (let i = 0; i < parts.length; i++) {
    acc += '/' + parts[i];
    if (i === parts.length - 1) {
      combineKey += parts[i];
    } else {
      combineKey += parts[i] + '_';
    }
    const raw = decodeURIComponent(parts[i]).replace(/[-_]+/g, ' ');
    const key = parts[i].toLowerCase();
    items.push({
      label:
        opts?.labelMap?.[key] ?? raw.charAt(0).toUpperCase() + raw.slice(1),
      href: base + acc,
    });
  }
  // 最后一项不需要 href
  if (items.length > 0) {
    items[items.length - 1].href = undefined;
  }
  return { items, combineKey };
}

/**
 * 判断路径是否包含完整域名
 */
function hasDomain(path: string): boolean {
  try {
    new URL(path);
    return true;
  } catch {
    return false;
  }
}

/**
 * 直接跳转到老项目（不经过中间件代理）
 */
export const navigateToOldProject = (
  path: string,
  target: string = '_self',
) => {
  const oldProjectDomain = getCurrentDomain(
    process.env.NEXT_PUBLIC_BRIDGE_STU_CENTER_DOMAIN,
  );

  if (oldProjectDomain) {
    let link = '';
    if (hasDomain(path)) {
      // 如果已经包含完整域名，直接使用
      link = getCurrentDomain(path);
    } else {
      // 如果是相对路径，拼接老项目域名
      link = `${oldProjectDomain}${path}`;
    }

    if (target === '_blank') {
      window.open(link, target);
      return;
    }

    window.location.href = link;
  } else {
    // eslint-disable-next-line no-console
    console.warn('NEXT_PUBLIC_BRIDGE_STU_CENTER_DOMAIN not configured');
    if (target === '_blank') {
      window.open(path, target);
      return;
    }

    window.location.href = path;
  }
};

export const getCurrentUrlInfoInServer = async () => {
  const currentUrl = await getBrowserUrl();
  const { path, originPath, originQuery } = parseUrl(currentUrl);
  const pathname = path;
  const lang = originPath.startsWith('/zh/') ? 'zh' : 'en';
  const search = originQuery.toString();

  return { pathname, lang, search };
};

export const getCurrentUrlInfoInClient = () => {
  const lang = window?.location?.pathname?.startsWith('/zh/') ? 'zh' : 'en';
  const pathname = window?.location?.pathname
    ?.replace('/zh/', '/')
    ?.replace('/en/', '/');
  const search = window?.location?.search;

  return { pathname, lang, search };
};

/**
 * 切换学员中心版本
 */
export const switchVersion = async ({
  addSwitchParam = true,
}: { addSwitchParam?: boolean } = {}) => {
  const oldVersionDomain = getCurrentDomain(
    process.env.NEXT_PUBLIC_LEGACY_STU_CENTER_DOMAIN,
  );

  const { lang, search, pathname } = getCurrentUrlInfoInClient();
  const oldVersionPath =
    NEW_VERSION_TO_OLD_VERSION_PATHS?.[
      pathname as keyof typeof NEW_VERSION_TO_OLD_VERSION_PATHS
    ] || '/home';
  const switchParam = search?.includes('?')
    ? '&switch-version=true'
    : '?switch-version=true';

  redirect(
    `${oldVersionDomain}/${lang}${oldVersionPath}${search}${addSwitchParam ? switchParam : ''}`,
  );
};

/**
 * 获取url的一级域名
 * @param url
 * @returns
 */
export function getPrimaryDomain(url: string): string {
  try {
    const { hostname } = new URL(url);

    // 特殊情况：localhost 或者 IP
    if (hostname === 'localhost' || /^[\d.]+$/.test(hostname)) {
      return hostname;
    }

    // 拆分域名
    const parts = hostname.split('.');

    if (parts.length <= 2) {
      // 例如 wukongedu.net
      return hostname;
    }

    // 返回倒数两段作为一级域名
    return parts.slice(-2).join('.');
  } catch (e) {
    throw new Error(`Invalid URL: ${url}`);
  }
}

/**
 * 获取浏览器url
 * @returns
 */
export const getBrowserUrl = async (): Promise<string> => {
  if (isServerSide()) {
    // 服务端：从 headers 获取
    try {
      const { headers } = await import('next/headers');
      const headersList = await headers();
      const url = headersList.get('x-url');
      return url || '';
    } catch {
      return '';
    }
  } else {
    // 客户端：从 location 获取
    return globalThis?.location?.href;
  }
};

/**
 * 解析url，返回域名、路径和查询参数
 * @param url
 * @returns
 */
export function parseUrl(url: string): ParsedUrl {
  try {
    const parsed = new URL(url);

    // 域名（含子域名，但不含协议和端口）
    const domain = parsed.hostname;

    // 路径（不含查询参数）
    const path = parsed.pathname.replace('/zh', '')?.replace('/en', '');

    // 查询参数（转成对象）
    const query: Record<string, string> = {};
    parsed.searchParams.forEach((value, key) => {
      query[key] = value;
    });

    return {
      domain,
      path,
      query,
      originQuery: parsed.searchParams,
      originPath: parsed.pathname,
    };
  } catch (error) {
    throw new Error(`Invalid URL: ${url}`);
  }
}

/**
 * 拼接url和searchParams，返回新的url
 * @param url url
 * @param searchParams searchParams
 * @returns url with searchParams
 */
export const connectSearchParams = (
  url: string,
  searchParams: Record<string, any>,
) => {
  if (!searchParams) {
    return url;
  }
  const parsedUrl = new URL(url);
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value || value === 0) {
      parsedUrl.searchParams.set(key, value);
    }
  });
  return parsedUrl.toString();
};

/**
 * 构建 App 中间页 URL（用于 H5 唤起 App）
 *
 * @param options - 配置选项
 * @param options.path - App 原生路径（如 'mine/sharePoster'）或 'OTHER_H5'
 * @param options.type - 跳转类型：'INTERNAL'（原生路径）| 'HTML'（H5页面）| 'APPLET'（小程序）
 * @param options.link - H5 页面地址的 base64 编码（当 type='HTML' 时必传）
 * @param options.restParams - 剩余参数，会被放入 customData
 * @returns 完整的 App 中间页 URL
 *
 * @example
 * // 1. 打开 App（不指定具体页面）
 * const url = buildAppMiddlePageUrl();
 * // 结果: https://student.wukongsch.com/app-middle-page
 *
 * @example
 * // 2. 打开 App 内部原生页面
 * const url = buildAppMiddlePageUrl({
 *   path: 'mine/sharePoster',
 *   type: 'INTERNAL',
 *   restParams: { trigger_event: 'C_Points_Mall_Home_Get_Points', pvid: '123' }
 * });
 * // 结果: https://student.wukongsch.com/app-middle-page?path=mine%2FsharePoster&type=INTERNAL&trigger_event=C_Points_Mall_Home_Get_Points&pvid=123
 *
 * @example
 * // 3. 打开 App 内部 H5 页面
 * const h5Url = 'https://test.wukongedu.net/wp/mall?type=email&studentCode=SQ00A2';
 * const encodedLink = btoa(h5Url);
 * const url = buildAppMiddlePageUrl({
 *   path: 'OTHER_H5',
 *   type: 'HTML',
 *   link: encodedLink,
 *   restParams: { studentCode: 'SQ00A2', l: 'xxx' }
 * });
 * // 结果: https://student.wukongsch.com/app-middle-page?path=OTHER_H5&type=HTML&link=aHR0cHM6...&studentCode=SQ00A2&l=xxx
 */
export function buildAppMiddlePageUrl(options?: {
  path?: string;
  type?: 'INTERNAL' | 'HTML' | 'APPLET';
  link?: string;
  restParams?: Record<string, string | number | boolean>;
}): string {
  const { path, type, link, restParams } = options || {};

  // 获取当前域名（处理线上环境 .sch.com / .edu.net 差异）
  const domain =
    getCurrentDomain(process.env.NEXT_PUBLIC_AUTH_REDIRECT_DOMAIN) ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  const query: Record<string, string> = {};

  // 添加必需参数
  if (path) {
    query.path = path;
  }
  if (type) {
    query.type = type;
  }
  if (link) {
    query.link = link;
  }

  // 添加剩余参数（会被 App 中间页放入 customData）
  if (restParams) {
    Object.entries(restParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query[key] = String(value);
      }
    });
  }

  return qs.stringifyUrl({
    url: `${domain}/app-middle-page`,
    query,
  });
}
